import React, { useRef, useState } from 'react';
import { isCycleDragForContract, readCycleDragPayload } from '../utils/cycleDragTransfer';
import useEditModeEnabled from './useEditModeEnabled';

type DropZoneProps = {
  onDragEnter: (event: React.DragEvent) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (event: React.DragEvent) => void;
};

// Makes an element accept a dragged cycle row (see cycleDragTransfer) that belongs
// to the given contract, and calls onDropCycle with the dropped cycle's id and the
// screen it was dragged from (null if it was screenless) so the caller can also
// refresh the source's cycle list.
export const useCycleDropTarget = (
  contractId: string | undefined,
  onDropCycle: (cycleId: string, sourceScreenId: string | null) => void,
): { isDragOver: boolean; dropZoneProps: DropZoneProps } => {
  const enabled = useEditModeEnabled();
  const [isDragOver, setIsDragOver] = useState(false);
  const depth = useRef(0);

  const onDragEnter = (event: React.DragEvent) => {
    if (!enabled || !contractId || !isCycleDragForContract(event, contractId)) return;
    depth.current += 1;
    setIsDragOver(true);
  };

  const onDragOver = (event: React.DragEvent) => {
    if (!enabled || !contractId || !isCycleDragForContract(event, contractId)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onDragLeave = () => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setIsDragOver(false);
  };

  const onDrop = (event: React.DragEvent) => {
    depth.current = 0;
    setIsDragOver(false);
    if (!enabled || !contractId) return;
    const payload = readCycleDragPayload(event, contractId);
    if (payload) {
      event.preventDefault();
      onDropCycle(payload.cycleId, payload.sourceScreenId);
    }
  };

  return { isDragOver, dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop } };
};
